/**
 * Ingestion Progress Message Component
 * Renders Basecamp ingestion progress in a compact, prototype-aligned style
 * Groups sub-steps and makes them collapsible
 */

import { useState } from 'react'
import React from 'react'
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, Inbox, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { testIds } from '@/lib/test-ids'
import { FileSummary } from './file-summary'
import type { Message } from '@/components/layout/chat-pane.types'
import {
  isIngestionErrorMessage,
  isIngestionSuccessMessage,
  sanitizeIngestionDisplayText,
} from '@/components/layout/chat-pane.utils'

interface IngestionStep {
  id: string
  label: string
  status: 'active' | 'completed'
  timestamp?: string
  details?: string
  subSteps?: IngestionStep[]
}

interface IngestionProgressMessageProps {
  content: string
  timestamp?: Date
  isLast?: boolean
}

// Parse ingestion message into structured steps
function parseIngestionMessage(content: string): IngestionStep[] {
  const steps: IngestionStep[] = []
  const lines = content.split('\n').filter(l => l.trim())

  let currentStep: IngestionStep | null = null
  let currentSubSteps: IngestionStep[] = []

  for (const line of lines) {
    const trimmed = line.trim()

    // Main step patterns:
    // "📋 **Step 1:** Extracting frontmatter..."
    // "🤖 **Generating markdown with Claude** (claude-sonnet-4-5-20250929)..."
    // "📥 **Fetching data from Basecamp API...**"
    // "📥 **Retrieved Basecamp data** (2006ms):"
    // "🔍 Starting ingestion of Basecamp todo **9176450356**..."
    const stepMatch = trimmed.match(/^([📋📁🤖💾📝📥⏱️🔍✅])\s\*\*(.+?)\*\*(.*)$/)
    if (stepMatch) {
      // Save previous step if exists
      if (currentStep) {
        if (currentSubSteps.length > 0) {
          currentStep.subSteps = currentSubSteps
        }
        steps.push(currentStep)
      }

      const [, icon, label, rest] = stepMatch
      currentStep = {
        id: `step-${steps.length}`,
        label: label.trim(),
        status: 'completed', // Assume completed if we're parsing it
        details: rest.trim() || undefined
      }
      currentSubSteps = []
      continue
    }

    // Sub-step patterns:
    // "- Converting HTML to markdown"
    // "📄 Reading ingestion rules..."
    // "🔄 Converting HTML to markdown..."
    // "🔍 Fetching context from FlowNotes database..."
    // "✅ Found 0 similar todos, 0 recent project docs"
    // "🤖 Calling Claude API..."
    const subStepMatch = trimmed.match(/^[-•]\s(.+)$/)
    const subStepEmojiMatch = trimmed.match(/^([📄🔄🔍✅🤖])\s(.+)$/)

    if (subStepMatch && currentStep) {
      currentSubSteps.push({
        id: `substep-${currentSubSteps.length}`,
        label: subStepMatch[1].trim(),
        status: 'completed'
      })
      continue
    } else if (subStepEmojiMatch && currentStep) {
      const [, emoji, label] = subStepEmojiMatch
      currentSubSteps.push({
        id: `substep-${currentSubSteps.length}`,
        label: label.trim(),
        status: 'completed'
      })
      continue
    }

    // Bullet list items under a step (like "**Title:** Review weekly timesheets")
    const bulletItemMatch = trimmed.match(/^\*\*(.+?):\*\*\s(.+)$/)
    if (bulletItemMatch && currentStep) {
      // Add to sub-steps as a detail item
      currentSubSteps.push({
        id: `substep-${currentSubSteps.length}`,
        label: `${bulletItemMatch[1]}: ${bulletItemMatch[2]}`,
        status: 'completed'
      })
      continue
    }

    // Italic text (like "_This may take 10-15 seconds..._") - skip
    if (trimmed.startsWith('_') && trimmed.endsWith('_')) {
      continue
    }
  }

  // Add last step
  if (currentStep) {
    if (currentSubSteps.length > 0) {
      currentStep.subSteps = currentSubSteps
    }
    steps.push(currentStep)
  }

  return steps
}

// Single step component (collapsible)
function IngestionStepItem({
  step,
  isExpanded,
  onToggle
}: {
  step: IngestionStep
  isExpanded: boolean
  onToggle: () => void
}) {
  const hasSubSteps = step.subSteps && step.subSteps.length > 0

  return (
    <div className="slide-in-from-bottom-2">
      <button
        onClick={onToggle}
        className="w-full py-1.5 flex items-center justify-between hover:bg-muted/30 rounded transition-colors"
      >
        <div className="flex items-center gap-2">
          {step.status === 'completed' ? (
            <CheckCircle2 className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
          ) : (
            <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground flex-shrink-0" />
          )}
          <span className="text-sm text-muted-foreground">{step.label}</span>
        </div>
        {hasSubSteps && step.status === 'completed' && (
          <ChevronDown className={cn(
            "w-3.5 h-3.5 transition-transform text-muted-foreground flex-shrink-0",
            isExpanded && "rotate-180"
          )} />
        )}
      </button>

      {isExpanded && hasSubSteps && (
        <div className="pl-6 pb-2 space-y-1 mt-1 animate-in fade-in slide-in-from-top-2">
          {step.subSteps!.map((subStep, idx) => (
            <div
              key={subStep.id}
              className="flex items-center gap-2 text-xs text-muted-foreground"
              style={{ animationDelay: `${idx * 30}ms` }}
            >
              <div className="w-1 h-1 rounded-full bg-muted-foreground/40" />
              <span>{subStep.label}</span>
            </div>
          ))}
        </div>
      )}

      {!isExpanded && hasSubSteps && step.status === 'completed' && (
        <div className="pl-6 pb-1 text-xs text-muted-foreground">
          ✓ {step.subSteps!.length} sub-steps completed
        </div>
      )}
    </div>
  )
}

export function IngestionProgressMessage({
  content,
  timestamp,
  isLast
}: IngestionProgressMessageProps) {
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set())

  const toggleStep = (stepId: string) => {
    setExpandedSteps(prev => {
      const newSet = new Set(prev)
      if (newSet.has(stepId)) {
        newSet.delete(stepId)
      } else {
        newSet.add(stepId)
      }
      return newSet
    })
  }

  // Parse content into steps
  const steps = parseIngestionMessage(content)

  // Auto-collapse all steps after completion (when isLast is true and all steps are completed)
  React.useEffect(() => {
    if (isLast && steps.length > 0 && steps.every(s => s.status === 'completed')) {
      // Collapse all steps by default after completion
      setExpandedSteps(new Set())
    }
  }, [isLast, content]) // Use content instead of steps to avoid infinite loop

  // If no structured steps found, render as simple markdown
  if (steps.length === 0) {
    return (
      <div className="space-y-2">
        <div className="prose prose-sm dark:prose-invert max-w-none prose-p:my-1 prose-pre:my-0 prose-pre:p-0 prose-pre:bg-transparent text-sm leading-relaxed">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {content}
          </ReactMarkdown>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-1.5">
      {steps.map((step, idx) => (
        <IngestionStepItem
          key={step.id}
          step={step}
          isExpanded={expandedSteps.has(step.id)}
          onToggle={() => toggleStep(step.id)}
        />
      ))}
    </div>
  )
}

// ============================================================================
// IngestionRunCard — collapsible group view for a full ingestion conversation
// ============================================================================

interface IngestionRunCardProps {
  messages: Message[]
  /**
   * When the final success/error assistant message is rendered outside this card, pass the same
   * summary used for the slim header so the row shows the filename / state while steps stay inside.
   */
  runSummary?: { label: string; status: 'running' | 'success' | 'error' }
  /** Open a created/updated work-spec from the FileSummary card (must open a tab, not only navigate). */
  onDocumentClick?: (documentIdOrPath: string, event?: React.MouseEvent) => void
}

/** Pull the success file path out of the `**Created:**` or `**Updated:**` line in a success message. */
function extractCreatedPath(content: string): string | null {
  const m = content.match(/\*\*(?:Created|Updated):\*\*\s*`([^`]+)`/)
  return m ? m[1] : null
}

function isNarrativeMessage(message: Message): boolean {
  const meta = (message as Message & { metadata?: { type?: string } }).metadata
  return meta?.type === 'ingestion-narrative'
}

function findLastNarrativeIndex(messages: Message[], beforeIdx?: number): number {
  const end = beforeIdx ?? messages.length
  for (let i = end - 1; i >= 0; i--) {
    if (isNarrativeMessage(messages[i])) return i
  }
  return -1
}

/** First sentence or line for compact run-card rows. */
function extractRowLabel(content: string): string {
  const firstParagraph = content.split('\n\n')[0]?.trim() ?? content.split('\n').find(l => l.trim())?.trim() ?? ''
  const plain = firstParagraph
    .replace(/\*\*/g, '')
    .replace(/`/g, '')
    .replace(/^(?:[\u{1F300}-\u{1FAFF}]|[\u{2600}-\u{27BF}])\s*/u, '')
    .trim()
  const sentence = plain.match(/^[^.!?]+[.!?]?/)?.[0]?.trim() ?? plain
  return sentence.slice(0, 120)
}

/** Build a friendly header label from the messages in the run (or pass a single terminal message). */
export function buildRunHeaderLabel(messages: Message[]): { label: string; status: 'running' | 'success' | 'error' } {
  const success = messages.find((m) => isIngestionSuccessMessage(m))
  if (success) {
    const path = extractCreatedPath(success.content)
    const filename = path ? path.split('/').pop() : null
    return { label: filename ?? 'Ingestion complete', status: 'success' }
  }
  const error = messages.find((m) => isIngestionErrorMessage(m))
  if (error) {
    return { label: 'Ingestion failed', status: 'error' }
  }
  return { label: 'Ingesting...', status: 'running' }
}

/** Render the success message body (file path, preview, refinement bullets) inside the expanded view. */
function SuccessMessageBody({
  message,
  onDocumentClick,
}: {
  message: Message
  onDocumentClick?: (documentIdOrPath: string, event?: React.MouseEvent) => void
}) {
  // Strip the **Files Changed:** trailer; FileSummary card replaces it.
  // parseFileSummary in assistant-message.tsx handles full path; here we just inline a simple strip.
  const stripped = sanitizeIngestionDisplayText(
    message.content
      .replace(/\n*---\n+\*\*Files Changed:\*\*[\s\S]*$/, '')
      .replace(/\n*\*\*Preview:\*\*\n```markdown\nPreview not available\n```/i, '')
      .trimEnd(),
  )

  // Build file summary entries from message.metadata (set by IngestionChatManager.addSuccess) or content.
  const meta = (message as any).metadata as
    | { type?: string; filePath?: string; fileName?: string; linesAdded?: number; linesRemoved?: number }
    | undefined
  let files: Array<{ path: string; name: string; linesAdded: number; linesRemoved?: number }> | null = null
  if (meta?.type === 'ingestion-success' && meta.filePath) {
    files = [{
      path: meta.filePath,
      name: meta.fileName ?? meta.filePath.split('/').pop() ?? meta.filePath,
      linesAdded: meta.linesAdded ?? 0,
      linesRemoved: meta.linesRemoved ?? 0,
    }]
  } else {
    const created = extractCreatedPath(message.content)
    const linesMatch = message.content.match(/\*\*Files Changed:\*\*\n- `[^`]+` \(\+(\d+)(?: -(\d+))?\)/)
    if (created) {
      files = [{
        path: created,
        name: created.split('/').pop() ?? created,
        linesAdded: linesMatch ? parseInt(linesMatch[1], 10) : 0,
        linesRemoved: linesMatch && linesMatch[2] ? parseInt(linesMatch[2], 10) : 0,
      }]
    }
  }

  return (
    <div className="space-y-2">
      <div className="prose prose-sm dark:prose-invert max-w-none prose-p:my-1 prose-pre:my-0 prose-pre:p-0 prose-pre:bg-transparent text-sm leading-relaxed">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{stripped}</ReactMarkdown>
      </div>
      {files && files.length > 0 && (
        <FileSummary
          files={files}
          onOpenFile={(filePath, event) => {
            void onDocumentClick?.(filePath, event)
          }}
        />
      )}
    </div>
  )
}

/**
 * Collapsible card that groups all consecutive assistant messages from a Basecamp ingestion
 * conversation into a single block. Auto-expanded while running, collapses to a slim row on
 * completion. Click header to toggle.
 */
export function IngestionRunCard({ messages, runSummary, onDocumentClick }: IngestionRunCardProps) {
  const { label, status } = runSummary ?? buildRunHeaderLabel(messages)
  const [expanded, setExpanded] = useState(false)

  // Auto-expand while running so progress is visible; collapse once we have a terminal message
  const isRunning = status === 'running'
  const isError = status === 'error'
  const isExpanded = isRunning || expanded
  // Header spinner only when collapsed; expanded rows show per-step spinners
  const showHeaderSpinner = isRunning && !isExpanded

  // Identify the success/error message so we can render its body in full instead of a short row
  const terminalIdx = messages.findIndex(
    (m) => isIngestionSuccessMessage(m) || isIngestionErrorMessage(m),
  )

  return (
    <div className="w-full min-w-0">
      <div>
        <div
          className={cn(
            'rounded-md border border-border/60 bg-muted/30 text-sm overflow-hidden',
            isError && 'border-amber-500/25 bg-amber-500/[0.04]'
          )}
          data-testid={testIds.chatPane.ingestionRunCard}
        >
          <button
            type="button"
            onClick={() => !isRunning && setExpanded(e => !e)}
            className={cn(
              'w-full flex items-center gap-2 px-3 py-1.5 text-left transition-colors',
              !isRunning && 'hover:bg-muted/60 cursor-pointer',
              isRunning && 'cursor-default'
            )}
            data-testid={testIds.chatPane.ingestionRunHeader}
          >
            <span className={cn('shrink-0 text-muted-foreground', isError && 'text-amber-500')}>
              {showHeaderSpinner
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                : isError
                  ? <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
                  : isRunning
                    ? <Inbox className="h-3.5 w-3.5" aria-hidden />
                    : <CheckCircle2 className="h-3.5 w-3.5 text-green-500/80" aria-hidden />
              }
            </span>
            <span className="min-w-0 flex-1 text-xs font-medium text-foreground/80 truncate">{label}</span>
            {!isRunning && (
              <ChevronRight
                className={cn(
                  'h-3.5 w-3.5 shrink-0 text-muted-foreground/50 transition-transform duration-150',
                  isExpanded && 'rotate-90'
                )}
                aria-hidden
              />
            )}
          </button>

          {isExpanded && (
            <div
              className="border-t border-border/40 divide-y divide-border/30"
              data-testid={testIds.chatPane.ingestionRunRows}
            >
              {messages.map((m, idx) => {
                // Render the terminal (success/error) message in full so file preview etc. stay visible
                if (idx === terminalIdx) {
                  return (
                    <div key={idx} className="px-3 py-2.5">
                      <SuccessMessageBody message={m} onDocumentClick={onDocumentClick} />
                    </div>
                  )
                }

                // Skip the user "Ingest Basecamp todo: <url>" priming row — already obvious from context
                if (m.role === 'user') return null

                if (isNarrativeMessage(m)) {
                  const activeNarrativeIdx = findLastNarrativeIndex(
                    messages,
                    terminalIdx >= 0 ? terminalIdx : undefined,
                  )
                  const stillRunning = status === 'running' && idx === activeNarrativeIdx
                  return (
                    <div key={idx} className="px-3 py-1">
                      <div className="flex gap-2">
                        <span className="shrink-0 flex h-[1.375rem] w-3.5 items-center justify-center">
                          {stillRunning
                            ? <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" aria-hidden />
                            : <CheckCircle2 className="h-3 w-3 text-green-500/80" aria-hidden />
                          }
                        </span>
                        <div className="prose prose-sm dark:prose-invert max-w-none prose-p:my-0 prose-p:leading-snug prose-pre:my-0 prose-pre:p-0 prose-pre:bg-transparent text-sm leading-snug text-muted-foreground min-w-0 flex-1">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>
                            {sanitizeIngestionDisplayText(m.content)}
                          </ReactMarkdown>
                        </div>
                      </div>
                    </div>
                  )
                }

                const rowLabel = extractRowLabel(sanitizeIngestionDisplayText(m.content))
                if (!rowLabel) return null

                // The very last assistant row (when terminal not yet present) is the one in flight
                const activeLegacyIdx = terminalIdx >= 0
                  ? terminalIdx - 1
                  : messages.length - 1
                const stillRunning = status === 'running' && idx === activeLegacyIdx

                return (
                  <div key={idx} className="flex gap-2 px-3 py-1 text-xs text-muted-foreground">
                    <span className="shrink-0 flex h-[1.125rem] w-3.5 items-center justify-center">
                      {stillRunning
                        ? <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
                        : <CheckCircle2 className="h-3 w-3 text-green-500/80" aria-hidden />
                      }
                    </span>
                    <span className="min-w-0 flex-1 truncate leading-[1.125rem]">{rowLabel}</span>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

